import MODULE_REGISTRY from "@/modules";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IConfig, TRouter } from "@type/router";

const initialState: TRouter = {
  menuConfig: [],
  currentPage: null,
};

/**
 * Enrich API config with frontend metadata from MODULE_REGISTRY.
 * Backend only sends: key, path, children, tabs (structural data).
 * Frontend adds: label, icon (from registry keyed by `key`).
 */
function enrichConfig(configs: IConfig[]): IConfig[] {
  return configs.map(config => {
    const entry = MODULE_REGISTRY[config.key];
    return {
      ...config,
      // Use registry label; fall back to backend-provided label (migration); fall back to key.
      // `||` (not `??`) so empty-string labels also fall through.
      label: entry?.label || config.label || config.key,
      // Use registry icon; fall back to backend-provided icon
      icon: entry?.icon ?? config.icon,
      // Enrich tabs
      tabs: config.tabs?.map(tab => ({
        ...tab,
        label: MODULE_REGISTRY[tab.key]?.label || tab.label || tab.key,
      })),
      // Recurse children
      children: config.children ? enrichConfig(config.children) : undefined,
    };
  });
}

const routerSlice = createSlice({
  name: "router",
  initialState,
  reducers: {
    updateRouterConfig(state, action: PayloadAction<IConfig[]>) {
      // First enrich with frontend metadata
      const enriched = enrichConfig(action.payload);

      // Determine the menu items to work with:
      // Backend may send either a flat array or a ROOT-wrapped structure
      const rootMenu = enriched.length === 1 && enriched[0]?.key === "ROOT" ? enriched[0] : null;
      const menuItems = sortHomeFirst(rootMenu?.children ?? enriched);

      // Find the homepage path for the index redirect
      const homePath = findPathByKey(menuItems, "HOMEPAGE") ?? findPathByKey(menuItems, "HOME");
      const redirectPath = homePath ?? findFirstLeafPath(menuItems);

      if (redirectPath) {
        const indexRoute: IConfig = {
          index: true,
          key: "__INDEX_REDIRECT__",
          label: "",
          path: "/",
          redirect: redirectPath,
        };

        if (rootMenu) {
          // ROOT-wrapped: inject into root's children
          rootMenu.children = [indexRoute, ...menuItems];
        } else {
          // Flat array: wrap everything in a synthetic ROOT node
          state.menuConfig = [
            {
              key: "ROOT",
              label: "",
              path: "/",
              children: [indexRoute, ...menuItems],
            },
          ];
          return;
        }
      }

      state.menuConfig = enriched;
    },
  },
});

/**
 * Sort menu items so HOMEPAGE/HOME always appears first.
 */
function sortHomeFirst(items: IConfig[]): IConfig[] {
  const homeIndex = items.findIndex(item => item.key === "HOMEPAGE" || item.key === "HOME");
  if (homeIndex <= 0) return items; // already first or not found
  const sorted = [...items];
  const [homeItem] = sorted.splice(homeIndex, 1);
  sorted.unshift(homeItem);
  return sorted;
}

/**
 * Recursively find a node by key and return its path.
 */
function findPathByKey(configs: IConfig[], targetKey: string): string | undefined {
  for (const config of configs) {
    if (config.key === targetKey && config.path) {
      return config.path;
    }
    if (config.children?.length) {
      const found = findPathByKey(config.children, targetKey);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Recursively find the first leaf node path.
 * A leaf is a node that has a component registered in MODULE_REGISTRY or has no children.
 */
function findFirstLeafPath(configs: IConfig[]): string | undefined {
  for (const config of configs) {
    // Check if this key has a component in the registry (i.e., it's a page that can be rendered)
    if (MODULE_REGISTRY[config.key]?.component && config.path) {
      return config.path;
    }
    if (config.children?.length) {
      const found = findFirstLeafPath(config.children);
      if (found) return found;
    }
  }
  return undefined;
}

export const { updateRouterConfig } = routerSlice.actions;
export default routerSlice.reducer;
